import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import List, { ListItem, ListItemProps } from "../List";
import { useFieldId, useFieldValidity } from "./utilities";
import FieldProps from "./FieldProps";
import FieldWrapper from "./components/FieldWrapper";

type CheckboxFieldProps<D> = FieldProps<Array<string>> & {
  options: Array<D>;
  onChangeOptions?: (options: Array<D>) => void;
  renderOption: (item: D) => ListItemProps;
  keyExtractor: (d: D) => string;
  renderEmpty?: () => ReactNode;
};

export default function CheckboxField<T>(props: CheckboxFieldProps<T>) {
  const {
    value,
    onChange,
    options,
    required = false,
    errorMessage,
    keyExtractor,
    renderOption,
    onChangeOptions,
    renderEmpty,
  } = props;

  const id = useFieldId(props.id);

  // Remove selected options out of the options list
  useEffect(() => {
    if (!onChange) return;
    const optionsKeys = options.map(keyExtractor);
    const inOptionValues = value.filter((v) => optionsKeys.includes(v));
    if (inOptionValues.join(",") === value.join(",")) return;
    onChange(inOptionValues);
  }, [value, keyExtractor, onChange]);

  const selectedOptions = useMemo(() => {
    const selectedOptions: Array<T> = [];
    value.forEach((v) => {
      const found = options.find((o) => keyExtractor(o) === v);
      if (found) selectedOptions.push(found);
    });
    return selectedOptions;
  }, [value, options]);

  // Trigger onChangeOptions
  useEffect(() => {
    if (!onChangeOptions) return;
    onChangeOptions(selectedOptions);
  }, [onChangeOptions, selectedOptions]);

  const validity = useFieldValidity(
    value,
    required,
    (v) => v === undefined || v === null || v.length === 0,
    (v) => {
      if (errorMessage) return errorMessage(v);
      return null;
    },
  );

  const onToggle = useCallback(
    (v: string) => {
      setFinalized(true);
      if (onChange) {
        const isIncluded = value.reduce(
          (acc, elem) => elem === v || acc,
          false,
        );
        if (isIncluded) {
          const newValue = value.filter((elem) => elem !== v);
          onChange(newValue);
        } else onChange([...value, v]);
      }
    },
    [onChange, value],
  );

  const inputNode: ReactNode = (
    <List
      noFrame
      data={options}
      renderEmpty={renderEmpty}
      renderItem={(i) => (
        <ListItem
          {...renderOption(i)}
          onClick={() => onToggle(keyExtractor(i))}
          icon={
            value.includes(keyExtractor(i)) ? "checkbox-on" : "checkbox-off"
          }
        />
      )}
    />
  );

  const [finalized, setFinalized] = useState(false);

  return (
    <FieldWrapper {...props} id={id} validity={validity} finalized={finalized}>
      {inputNode}
    </FieldWrapper>
  );
}
